// Motor del "Mapa de Movimientos" inteligente: detección de tipo, conversión
// de moneda y construcción del grafo (React Flow) con totales por mes y globales.

export const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Palabras clave para inferir ingreso vs egreso a partir del concepto.
const INGRESO_KW = [
  "sueldo", "salario", "venta", "vendi", "vendí", "cobro", "cobre", "cobré",
  "ingreso", "deposito", "depósito", "abono", "honorario", "comision recibida",
  "reembolso", "premio", "interes", "interés", "renta recibida", "propina",
  "bono", "regalo", "aporte", "recibi", "recibí", "pago recibido", "dividendo",
];
const EGRESO_KW = [
  "compra", "compre", "compré", "pago", "pague", "pagué", "gasto", "gaste", "gasté",
  "alquiler", "renta", "comida", "alimento", "mercado", "super", "transporte",
  "gasolina", "servicio", "luz", "agua", "internet", "telefono", "teléfono",
  "factura", "deuda", "prestamo", "préstamo", "salud", "medicina", "farmacia",
  "ropa", "educacion", "educación", "colegio", "impuesto", "suscripcion",
  "suscripción", "comision bancaria", "envio", "envío", "delivery",
];

// Devuelve "ingreso" | "egreso" | null (null = ambiguo, se debe preguntar).
export function guessType(concepto) {
  const t = (concepto || "").toLowerCase();
  if (!t.trim()) return null;
  const hitIn = INGRESO_KW.some((k) => t.includes(k));
  const hitEg = EGRESO_KW.some((k) => t.includes(k));
  if (hitIn && !hitEg) return "ingreso";
  if (hitEg && !hitIn) return "egreso";
  return null;
}

export const CURRENCY_SYMBOL = { USD: "$", VES: "Bs" };

// Categorías predefinidas para clasificar el movimiento de forma fiable.
// Elegir una categoría fija el tipo (ingreso/egreso) sin depender de palabras clave.
export const CATEGORIES = [
  { id: "sueldo", label: "Sueldo / Salario", tipo: "ingreso" },
  { id: "venta", label: "Venta", tipo: "ingreso" },
  { id: "honorarios", label: "Honorarios / Servicios", tipo: "ingreso" },
  { id: "bono", label: "Bono / Comisión", tipo: "ingreso" },
  { id: "reembolso", label: "Reembolso", tipo: "ingreso" },
  { id: "regalo", label: "Regalo / Aporte", tipo: "ingreso" },
  { id: "intereses", label: "Intereses / Dividendos", tipo: "ingreso" },
  { id: "otro_ingreso", label: "Otro ingreso", tipo: "ingreso" },
  { id: "vivienda", label: "Vivienda / Alquiler", tipo: "egreso" },
  { id: "alimentacion", label: "Alimentación", tipo: "egreso" },
  { id: "transporte", label: "Transporte", tipo: "egreso" },
  { id: "servicios", label: "Servicios (luz, agua, internet)", tipo: "egreso" },
  { id: "salud", label: "Salud", tipo: "egreso" },
  { id: "educacion", label: "Educación", tipo: "egreso" },
  { id: "ropa", label: "Ropa / Personal", tipo: "egreso" },
  { id: "ocio", label: "Ocio / Entretenimiento", tipo: "egreso" },
  { id: "deudas", label: "Deudas / Préstamos", tipo: "egreso" },
  { id: "impuestos", label: "Impuestos", tipo: "egreso" },
  { id: "otro_egreso", label: "Otro egreso", tipo: "egreso" },
];

export function categoryById(id) {
  return CATEGORIES.find((c) => c.id === id) || null;
}
export function categoryLabel(id) {
  const c = categoryById(id);
  return c ? c.label : "";
}

// Convierte un monto a la moneda de visualización. rate = Bs por 1 $.
export function convert(monto, from, to, rate) {
  const n = Number(monto) || 0;
  if (from === to) return n;
  const r = Number(rate) || 0;
  if (!r) return n; // sin tasa: se muestra tal cual (se avisa en la UI)
  if (from === "USD" && to === "VES") return n * r;
  if (from === "VES" && to === "USD") return n / r;
  return n;
}

// Busca la tasa (Bs por $) vigente en una fecha dada dentro del histórico asc.
export function rateForDate(history, dateStr) {
  if (!history || !history.length || !dateStr) return null;
  const d = String(dateStr).slice(0, 10);
  let best = null;
  for (const h of history) {
    if (h.date <= d) best = h.rate;
    else break;
  }
  return best != null ? best : history[0].rate;
}

export function fmtMoney(value, currency) {
  const sym = CURRENCY_SYMBOL[currency] || "";
  const v = Math.round((Number(value) || 0) * 100) / 100;
  const s = v.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === "VES" ? `${s} ${sym}` : `${sym}${s}`;
}

// Parsea "YYYY-MM-DD" como fecha LOCAL (evita el desfase de -1 día por UTC).
export function parseLocalDate(str) {
  if (!str) return new Date();
  const s = String(str).slice(0, 10);
  const [y, m, d] = s.split("-").map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return new Date(str);
}

export function yearsFromMovements(movements, createdAt) {
  const set = new Set();
  const startY = createdAt ? new Date(createdAt).getFullYear() : new Date().getFullYear();
  const nowY = new Date().getFullYear();
  for (let y = startY; y <= nowY; y++) set.add(y);
  (movements || []).forEach((m) => { if (m.fecha) set.add(parseLocalDate(m.fecha).getFullYear()); });
  return Array.from(set).sort((a, b) => b - a);
}

const COL_X = 300;

// Estima la altura visual de un nodo de movimiento para espaciarlos sin solaparse.
function estMovHeight(m) {
  let h = 58; // título + monto + fecha
  if (m.categoria) h += 20;
  if (m.descripcion) h += 22;
  if ((m.concepto || "").length > 24) h += 16;
  if (m.moneda && m.moneda !== "USD") h += 14;
  return h;
}

// Totales de TODO el mapa (todos los años), convirtiendo cada movimiento con
// la tasa de su fecha (histórica si es pasada). Devuelve {totalIn, totalEg, haber, count}.
export function computeTotals(movements, { displayCurrency, rate, history } = {}) {
  const disp = displayCurrency || "USD";
  const today = new Date().toISOString().slice(0, 10);
  const rateFor = (m) => {
    const d = (m.fecha || "").slice(0, 10);
    if (d && d < today) { const hr = rateForDate(history, d); if (hr) return hr; }
    return rate;
  };
  let totalIn = 0, totalEg = 0;
  for (const m of movements || []) {
    const v = convert(m.monto, m.moneda || "USD", disp, rateFor(m));
    if (m.tipo === "ingreso") totalIn += v;
    else if (m.tipo === "egreso") totalEg += v;
  }
  return { totalIn, totalEg, haber: totalIn - totalEg, count: (movements || []).length };
}

// Construye {nodes, edges} para React Flow del año seleccionado.
export function buildMoveMap({ movements, year, displayCurrency, rate, createdAt, history }) {
  const disp = displayCurrency || "USD";
  const inYear = (movements || []).filter((m) => m.fecha && parseLocalDate(m.fecha).getFullYear() === year);

  // Agrupar por mes (0-11)
  const byMonth = {};
  for (const m of inYear) {
    const mi = parseLocalDate(m.fecha).getMonth();
    (byMonth[mi] ||= []).push(m);
  }

  const today = new Date().toISOString().slice(0, 10);
  const rateFor = (m) => {
    const mdate = (m.fecha || "").slice(0, 10);
    if (mdate && mdate < today) {
      const hr = rateForDate(history, mdate);
      if (hr) return hr;
    }
    return rate;
  };
  const conv = (m) => convert(m.monto, m.moneda || "USD", disp, rateFor(m));
  let totalIn = 0, totalEg = 0;
  for (const m of inYear) {
    if (m.tipo === "ingreso") totalIn += conv(m);
    else if (m.tipo === "egreso") totalEg += conv(m);
  }
  const haber = totalIn - totalEg;

  const nodes = [];
  const edges = [];
  const now = new Date().toISOString();
  const edge = (s, t, color) => edges.push({
    id: `e-${s}-${t}`, source: s, target: t, type: "smoothstep",
    style: { stroke: color, strokeWidth: 2 }, markerEnd: { type: "arrowclosed", color },
  });

  // Layout: hojas primero (movimientos) para centrar a los padres. Jerarquía: mes -> día -> movimiento.
  let cursor = 0;
  const GAP = 26;      // entre movimientos del mismo día
  const GAP_DAY = 48;  // separación entre días distintos
  const GAP_MONTH = 90; // separación entre meses distintos
  const monthIdxs = Object.keys(byMonth).map(Number).sort((a, b) => a - b);

  const monthNodes = [];
  for (const mi of monthIdxs) {
    const monItems = byMonth[mi];
    const byDay = {};
    for (const m of monItems) {
      const dnum = parseLocalDate(m.fecha).getDate();
      (byDay[dnum] ||= []).push(m);
    }
    const dayNums = Object.keys(byDay).map(Number).sort((a, b) => a - b);
    const monId = `month-${year}-${mi}`;
    let mIn = 0, mEg = 0;
    const dayCenters = [];

    for (const dnum of dayNums) {
      const items = byDay[dnum].slice().sort((a, b) => parseLocalDate(a.fecha) - parseLocalDate(b.fecha));
      const centers = [];
      let dIn = 0, dEg = 0;
      for (const m of items) {
        const h = estMovHeight(m);
        const top = cursor;
        centers.push(top + h / 2);
        cursor += h + GAP;
        const amount = conv(m);
        if (m.tipo === "ingreso") { mIn += amount; dIn += amount; }
        else if (m.tipo === "egreso") { mEg += amount; dEg += amount; }
        const nid = `mov-${m.id}`;
        const isCambio = m.tipo === "cambio";
        nodes.push({
          id: nid, type: "movenode", position: { x: COL_X * 5, y: top },
          data: {
            variant: m.tipo === "ingreso" ? "ingreso" : isCambio ? "cambio" : "egreso",
            title: m.concepto || (isCambio ? "Cambio de moneda" : "(sin concepto)"),
            subtitle: isCambio
              ? `${fmtMoney(m.monto, m.moneda || "USD")} → ${fmtMoney(m.montoDestino ?? m.monto, m.monedaDestino || m.moneda)}`
              : fmtMoney(conv(m), disp),
            category: isCambio ? "Cambio" : categoryLabel(m.categoria),
            desc: m.descripcion || "",
            note: isCambio ? "No afecta ingresos/egresos" : (m.moneda && m.moneda !== disp ? `${fmtMoney(m.monto, m.moneda)} orig.` : ""),
            createdAt: m.fecha,
          },
        });
      }
      const dayCenter = (centers[0] + centers[centers.length - 1]) / 2;
      cursor += GAP_DAY - GAP; // gap extra tras cerrar el día
      dayCenters.push(dayCenter);
      const dt = parseLocalDate(items[0].fecha);
      const wd = dt.toLocaleDateString("es-VE", { weekday: "short" }).replace(".", "");
      const dayId = `day-${year}-${mi}-${dnum}`;
      nodes.push({
        id: dayId, type: "movenode", position: { x: COL_X * 4, y: dayCenter - 30 },
        data: {
          variant: "day",
          title: `${wd} ${dnum}`,
          subtitle: fmtMoney(dIn - dEg, disp),
          saldoPositive: dIn - dEg >= 0,
        },
      });
      edge(monId, dayId, "#f59e0b");
      for (const m of items) edge(dayId, `mov-${m.id}`, m.tipo === "ingreso" ? "#10b981" : m.tipo === "cambio" ? "#0ea5e9" : "#f43f5e");
    }

    const monthCenter = dayCenters.length ? (dayCenters[0] + dayCenters[dayCenters.length - 1]) / 2 : cursor;
    if (!dayCenters.length) cursor += 80;
    monthNodes.push({ id: monId, c: monthCenter });
    nodes.push({
      id: monId, type: "movenode", position: { x: COL_X * 3, y: monthCenter - 44 },
      data: {
        variant: "month",
        title: MONTHS_ES[mi],
        lines: [
          `Ingresos: ${fmtMoney(mIn, disp)}`,
          `Egresos: ${fmtMoney(mEg, disp)}`,
          `Saldo: ${fmtMoney(mIn - mEg, disp)}`,
        ],
        saldoPositive: mIn - mEg >= 0,
      },
    });
    cursor += GAP_MONTH; // separación antes del siguiente mes
  }

  // Nodo del año (naranja)
  const yearCenter = monthNodes.length
    ? (monthNodes[0].c + monthNodes[monthNodes.length - 1].c) / 2
    : 0;
  const yearY = yearCenter - 30;
  const yearId = `year-${year}`;
  nodes.push({
    id: yearId, type: "movenode", position: { x: COL_X * 2, y: yearY },
    data: { variant: "year", title: `Balance Neto ${year}`, subtitle: `${fmtMoney(haber, disp)} · ${inYear.length} mov.` },
  });
  for (const mn of monthNodes) edge(yearId, mn.id, "#f97316");

  // Nodos resumen (columna 1): Ingresos, Egresos, Haber
  const gap = 150;
  const ingY = yearY - gap, egY = yearY, habY = yearY + gap;
  nodes.push({ id: "sum-in", type: "movenode", position: { x: COL_X, y: ingY }, data: { variant: "ingreso", big: true, title: "Ingresos", subtitle: fmtMoney(totalIn, disp) } });
  nodes.push({ id: "sum-eg", type: "movenode", position: { x: COL_X, y: egY }, data: { variant: "egreso", big: true, title: "Egresos", subtitle: fmtMoney(totalEg, disp) } });
  nodes.push({ id: "sum-hb", type: "movenode", position: { x: COL_X, y: habY }, data: { variant: "haber", big: true, title: "Haber", subtitle: fmtMoney(haber, disp), saldoPositive: haber >= 0 } });

  // Raíz MOVIMIENTO (azul)
  nodes.push({ id: "root", type: "movenode", position: { x: 0, y: yearY }, data: { variant: "root", title: "Flujo económico", subtitle: `${CURRENCY_SYMBOL[disp]} · ${year}` } });

  edge("root", "sum-in", "#6366f1");
  edge("root", "sum-eg", "#6366f1");
  edge("root", "sum-hb", "#6366f1");
  edge("sum-in", yearId, "#10b981");
  edge("sum-eg", yearId, "#f43f5e");
  edge("sum-hb", yearId, "#94a3b8");

  return { nodes, edges, totals: { totalIn, totalEg, haber, count: inYear.length } };
}
